import {
  Breadcrumbs,
  Button,
  Link,
  Page,
  PageBody,
  PageBreadcrumbs,
  PageHeader,
  PageHeaderTitle,
  PageHeaderActions,
  PageHeaderDescription,
  Banner,
  Stack,
  Spinner,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Navigate } from '@/utils/navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { AIBusinessProfileWrapper } from '@/features/ai-business-profile/components';
import { useAIContextOverrides } from '@/features/ai-business-profile/hooks';
import { isAPIError } from '@/services/api/ts-rest';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { countFieldStats } from '@/features/ai-business-profile/helpers/form';

const AIBusinessProfilePageLayout = ({ children }: { children: React.ReactNode }) => {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const breadcrumbs = [
    { text: 'Workspace settings', href: `/workspaces/${workspaceId}/settings` },
    { text: 'AI business profile' },
  ];

  return (
    <Page style={{ width: '100%' }}>
      <PageBreadcrumbs>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </PageBreadcrumbs>
      {children}
    </Page>
  );
};

export const AIBusinessProfile = () => {
  const { hasPermission } = usePermissions();
  const { data, isLoading, error, refetch } = useAIContextOverrides();
  const formId = 'ai-business-profile-form';
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const { track } = useAnalytics();

  usePageViewTracking(() => {
    const stats = countFieldStats(data?.body.ai_context, data?.body.overrides);
    track('aiProfile', 'viewed', {
      'Fields Total': stats.total,
      'Fields AI Populated': stats.aiPopulated,
      'Fields User Overridden': stats.userOverridden,
      'Fields Empty': stats.empty,
    });
  }, data != null);

  if (!hasPermission('journeys/workspace_settings/view')) {
    return <Navigate to={`/workspaces/${workspaceId}/settings`} replace={true} />;
  }

  const canEdit = hasPermission('journeys/workspace_settings/edit');

  if (error != null) {
    return (
      <AIBusinessProfilePageLayout>
        <PageBody>
          <ErrorState error={error} onRetry={refetch} />
        </PageBody>
      </AIBusinessProfilePageLayout>
    );
  }

  if (isLoading === true) {
    return (
      <AIBusinessProfilePageLayout>
        <PageBody>
          <Stack alignBlock="center" alignInline="center" mt="3200">
            <Spinner />
          </Stack>
        </PageBody>
      </AIBusinessProfilePageLayout>
    );
  }

  if (data == null) {
    return null;
  }

  return (
    <AIBusinessProfilePageLayout>
      <AIBusinessProfileWrapper
        aiContext={data.body.ai_context}
        overrides={data.body.overrides}
        formId={formId}
        canEdit={canEdit}
        onEditIntent={(fieldName) => {
          track('aiProfile', 'editIntent', {
            Field: fieldName,
          });
        }}
        onSave={(changeInfo) => {
          track('aiProfile', 'saved', {
            'Fields Changed Count': changeInfo.fieldsChangedCount,
            'Fields Added Count': changeInfo.fieldsAddedCount,
            'Fields Edited Count': changeInfo.fieldsEditedCount,
            'Fields Cleared Count': changeInfo.fieldsClearedCount,
            'Changed Fields': changeInfo.changedFields,
            'Edit Session Ms': changeInfo.editSessionMs,
          });
        }}
        onDiscard={(unsavedFieldsChangedCount) => {
          track('aiProfile', 'discarded', {
            'Unsaved Fields Changed Count': unsavedFieldsChangedCount,
          });
        }}
      >
        {(formState) => (
          <>
            <PageHeader>
              <PageHeaderTitle>AI business profile</PageHeaderTitle>
              {canEdit ? (
                <PageHeaderActions>
                  <Button
                    variant="primary"
                    type="submit"
                    form={formId}
                    isDisabled={formState.isDirty !== true || formState.isValid !== true}
                  >
                    Save
                  </Button>
                </PageHeaderActions>
              ) : null}
              <PageHeaderDescription>
                Provide context about your business to help AI features deliver more relevant, on-brand content.{' '}
                <Link href="https://docs.customer.io/ai/cio-with-llms/" isExternal variant="secondary">
                  Learn more about Customer.io's AI philosophy
                </Link>
              </PageHeaderDescription>
            </PageHeader>
            <PageBody pb="1000">{formState.formContent}</PageBody>
          </>
        )}
      </AIBusinessProfileWrapper>
    </AIBusinessProfilePageLayout>
  );
};

const ErrorState = ({ error, onRetry }: { error: unknown; onRetry: () => unknown }) => {
  const getErrorMessage = (error: unknown): string => {
    if (isAPIError(error)) {
      const body = error.response.body as Record<string, unknown>;
      if (typeof body === 'object' && body !== null) {
        const errors = body.errors as Array<{ detail?: string }> | undefined;
        if (errors?.[0]?.detail != null) {
          return errors[0].detail;
        }
        const message = body.message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred while loading your AI business profile.';
  };

  return (
    <Stack>
      <Banner
        variant="error"
        title="Unable to load AI business profile"
        action={{
          label: 'Try again',
          onClick: onRetry,
          variant: 'subtle',
        }}
      >
        {getErrorMessage(error)}
      </Banner>
    </Stack>
  );
};
